/**
 * Hydration entry.
 *
 * The document already carries the rendered route, the server-provided
 * configuration, and the data the server fetched. Hydration adopts all three
 * rather than re-fetching, so the page does not flash a skeleton over content a
 * crawler and a human have already been given.
 */

import { StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { App } from "./App.js";
import { AnnouncerProvider } from "./components/Announcer.js";
import { StorefrontProvider, type Preload } from "./app/StorefrontContext.js";
import { readStorefrontConfig } from "./lib/config.js";
import { createHttpTransport } from "./lib/httpTransport.js";
import { parseItemList, parsePublicItem } from "./lib/publicContract.js";
import { parseRoute } from "./lib/routes.js";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/app.css";
import "./styles/garage.css";

const PRELOAD_ELEMENT_ID = "lp-storefront-preload";

/**
 * The preload block is parsed with the same validators core responses go
 * through. The server produced it, but it arrives as text in a document, and a
 * value that skipped the public contract on the way in would be a way around
 * `publicContract.ts` entirely.
 */
function readPreload(): Preload | null {
  const element = document.getElementById(PRELOAD_ELEMENT_ID);
  if (element === null || element.textContent === null || element.textContent.length === 0) {
    return null;
  }
  let raw: unknown;
  try {
    raw = JSON.parse(element.textContent);
  } catch {
    return null;
  }
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const path = record["path"];
  if (typeof path !== "string" || path.length === 0 || path.length > 2_048) return null;

  if (record["items"] !== undefined) {
    const parsed = parseItemList({ items: record["items"] });
    return parsed.ok ? { path, items: parsed.value } : null;
  }
  if (record["item"] !== undefined) {
    const parsed = parsePublicItem(record["item"]);
    return parsed.ok ? { path, item: parsed.value } : null;
  }
  return { path };
}

const root = document.getElementById("root");
if (root !== null) {
  hydrateRoot(
    root,
    <StrictMode>
      <StorefrontProvider
        config={readStorefrontConfig(document)}
        initialRoute={parseRoute(window.location.pathname, window.location.search)}
        interactive
        preload={readPreload()}
        transport={createHttpTransport()}
      >
        <AnnouncerProvider>
          <App />
        </AnnouncerProvider>
      </StorefrontProvider>
    </StrictMode>
  );
}
